// Libs
import classNames from 'classnames/bind';
import { Box, Button, Stack } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormProvider, useForm, UseFormReturn } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseToolbar, Loading } from '~/components/common';
import {
  AdditionalChargesOrderForm,
  AttachmentsOrderForm,
  ClientInformationOrderForm,
  EventDetailsOrderForm,
  EventTimelineOrderForm,
  GroupsOrderForm,
  NotesOrderForm,
  OrderItemsForm,
  SummaryOrderForm,
  TemplateOrderForm,
} from '~/components/module/order';
// Others
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { IBaseAddress, IBodyUpdate } from '~/utils/interface/common';
import { getOrderDetail, updateOrder } from '~/thunks/order/orderThunk';
import { BACK_TO_PREVIOUS_PAGE, EMPTY_STRING } from '~/utils/constants/common';
import { DiscountTypeEnum, TypeListInventory, TypeOrderDetailEnum } from '~/utils/enum/common';
import { cleanObject, convertTimeToHHmm, createFormData, getFullName } from '~/utils/helper/common';
import { convertDataDetailOrdersForm, defaultValuesOrdersForm, schema } from './helper';
import { IFormOrder, IOrderDetail, IOrderItem, IOrderItemAddOn, IParamQueryOrderDetail } from '~/utils/interface/order';
// Styles, images, icons
import styles from './OrderForm.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const OrderForm = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/order', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: orderId } = useParams();

  const {
    control,
    setValue,
    handleSubmit,
    watch,
    resetField,
    reset,
    trigger,
    setError,
    getValues,
    formState: { errors, isDirty, dirtyFields, touchedFields, isSubmitted, defaultValues },
  } = useForm<IFormOrder>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesOrdersForm,
  });

  const { listOrderItems, orderItems, listOrderItemsSelected } = watch();
  //#endregion Declare Hook

  //#region Selector
  const { profile } = useAppSelector((store) => store.user);
  //#endregion Selector

  //#region Declare State
  const [initialData, setInitialData] = useState<IFormOrder>(defaultValuesOrdersForm);

  const [managedByNameDefault, setManagedByNameDefault] = useState<string>(EMPTY_STRING);
  const [salesPersonNameDefault, setSalesPersonNameDefault] = useState<string>(EMPTY_STRING);
  const [couponNameDefault, setCouponNameDefault] = useState<string>(EMPTY_STRING);

  const [removeFileIds, setRemoveFileIds] = useState<string[]>([]);
  const [removeCustomIds, setRemoveCustomIds] = useState<string[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    setValue('managedBy', profile?.id);
    setValue('salesAccountId', profile?.id);
  }, [profile]);

  useEffect(() => {
    if (!orderId) return;

    const params: IParamQueryOrderDetail = {
      orderId: orderId,
      type: TypeOrderDetailEnum.OVERVIEW,
    };

    handleGetDetailOrder(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    if (!orderId && listOrderItemsSelected && listOrderItemsSelected?.length > 0) return;

    const listQuoteItemsSelectedData = listOrderItems?.filter((item) =>
      orderItems?.find(
        (selected) =>
          selected.inventoryId == item.id && (selected.variantId || EMPTY_STRING) === (item.variantId || EMPTY_STRING)
      )
    );

    setValue('listOrderItemsSelected', listQuoteItemsSelectedData);
  }, [listOrderItems]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailOrder = async (params: IParamQueryOrderDetail) => {
    setIsLoading(true);

    dispatch(getOrderDetail(params))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const dataDetail = res?.data as IOrderDetail;

        const body: IFormOrder = convertDataDetailOrdersForm(dataDetail);

        setManagedByNameDefault(
          getFullName({ firstName: dataDetail?.managedBy?.firstName, lastName: dataDetail?.managedBy?.lastName })
        );

        setSalesPersonNameDefault(
          getFullName({ firstName: dataDetail?.salesPerson?.firstName, lastName: dataDetail?.salesPerson?.lastName })
        );

        setCouponNameDefault(
          `${dataDetail?.orderCoupon?.name} - ${handleConvertNameCoupon(
            String(dataDetail?.orderCoupon?.discountRate),
            String(dataDetail?.orderCoupon?.discountType)
          )}`
        );

        setInitialData(body);
        reset(body);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleConvertNameCoupon = (discountRate: string, discountType: string) => {
    switch (discountType) {
      case DiscountTypeEnum.PERCENTAGE:
        return `${discountRate}%`;
      case DiscountTypeEnum.AMOUNT:
        return `$${discountRate}`;
      default:
        return EMPTY_STRING;
    }
  };

  const updateOverbooking = (data: IFormOrder) => {
    const { orderItems = [], orderAddOns = [] } = data;

    type ItemWithRef = {
      quantity: number;
      availableInventory: number;
      type?: string;
      orderItemIndex: number;
      orderAddOnIndex: number;
    };

    const key = (inventoryId: string, variantId: string) => `${inventoryId}|${variantId || EMPTY_STRING}`;

    const entries: { key: string; orderItemIndex: number; orderAddOnIndex: number }[] = [];
    orderItems.forEach((item, i) => {
      if (item?.inventoryId && item?.type !== TypeListInventory.PACKAGE)
        entries.push({
          key: key(item.inventoryId, item?.variantId || EMPTY_STRING),
          orderItemIndex: i,
          orderAddOnIndex: -1,
        });
    });
    orderAddOns.forEach((item, i) => {
      if (item?.inventoryId && item?.type !== TypeListInventory.PACKAGE)
        entries.push({
          key: key(item.inventoryId, item?.variantId || EMPTY_STRING),
          orderItemIndex: -1,
          orderAddOnIndex: i,
        });
    });

    const orderItemOverbooked = new Map<number, number>();
    const orderAddOnOverbooked = new Map<number, number>();

    const groupByKey = new Map<string, ItemWithRef[]>();
    entries.forEach(({ key: k, orderItemIndex, orderAddOnIndex }) => {
      const item = orderItemIndex >= 0 ? orderItems[orderItemIndex] : orderAddOns[orderAddOnIndex];
      const qty = Number(item?.quantity ?? 0);
      const available = Number(item?.availableInventory ?? 0);
      if (!groupByKey.has(k)) groupByKey.set(k, []);
      groupByKey.get(k)!.push({
        quantity: qty,
        availableInventory: available,
        type: item?.type,
        orderItemIndex,
        orderAddOnIndex,
      });
    });

    groupByKey.forEach((items) => {
      items.sort((a, b) => {
        const aOrder = a.orderItemIndex >= 0 ? a.orderItemIndex : orderItems.length + a.orderAddOnIndex;
        const bOrder = b.orderItemIndex >= 0 ? b.orderItemIndex : orderItems.length + b.orderAddOnIndex;
        return aOrder - bOrder;
      });

      const initialOverbooked = items.reduce((sum, entry) => {
        const origItem =
          entry.orderItemIndex >= 0 ? orderItems[entry.orderItemIndex] : orderAddOns[entry.orderAddOnIndex];
        return sum + Number(origItem?.overbookedQuantity ?? 0);
      }, 0);

      const available = Math.max(0, Number(items[0]?.availableInventory ?? 0) - initialOverbooked);
      let remaining = available;
      items.forEach((entry) => {
        const overbooked = Math.max(0, entry.quantity - remaining);
        remaining = Math.max(0, remaining - entry.quantity);
        if (entry.orderItemIndex >= 0) orderItemOverbooked.set(entry.orderItemIndex, overbooked);
        else orderAddOnOverbooked.set(entry.orderAddOnIndex, overbooked);
      });
    });

    const updatedOrderItems = orderItems.map((item, i) => {
      if (!item.inventoryId || item?.type === TypeListInventory.PACKAGE)
        return { ...item, isOverbooking: false, overbookedQuantity: 0 };
      const overbooked = orderItemOverbooked.get(i) ?? 0;
      return {
        ...item,
        isOverbooking: overbooked > 0,
        overbookedQuantity: overbooked,
      };
    });

    const updatedOrderAddOns = orderAddOns.map((item, i) => {
      if (!item.inventoryId || item?.type === TypeListInventory.PACKAGE)
        return { ...item, isOverbooking: false, overbookedQuantity: 0 };
      const overbooked = orderAddOnOverbooked.get(i) ?? 0;
      return {
        ...item,
        isOverbooking: overbooked > 0,
        overbookedQuantity: overbooked,
      };
    });

    return {
      ...data,
      orderItems: updatedOrderItems,
      orderAddOns: updatedOrderAddOns,
      isOverbooking: [...updatedOrderItems, ...updatedOrderAddOns].some((i) => i?.isOverbooking),
    };
  };

  const handleFormOrder = async (data: IFormOrder) => {
    setIsLoading(true);

    const checkedData = updateOverbooking(data);

    const {
      listOrderItems,
      selectGroupOrder,
      selectGroupItem,
      selectIdProductOrder,
      selectIdProductGroup,
      selectIdVariantOrder,
      selectIdVariantGroup,
      selectQuantityProduct,
      deleteProduct,
      deleteChildrenProduct,
      // businessType,
      customFees,
      couponType,
      templateOrderId,
      couponRate,
      orderFile,
      status,
      amountDue,
      couponAmount,
      clientAddress,
      listOrderItemsSelected,
      orderItemQuantity,
      ...restData
    } = checkedData;

    const convertData: IFormOrder = {
      ...restData,
      earliestDeliveryTime: convertTimeToHHmm(restData?.earliestDeliveryTime) || undefined,
      latestPickupTime: convertTimeToHHmm(restData?.latestPickupTime) || undefined,
      endTime: convertTimeToHHmm(restData?.endTime) || undefined,
      startTime: convertTimeToHHmm(restData?.startTime) || undefined,
      pickupScheduledTime: convertTimeToHHmm(restData?.pickupScheduledTime) || undefined,
      deliveryScheduledTime: convertTimeToHHmm(restData?.deliveryScheduledTime) || undefined,
      paymentTerms: restData?.paymentTerms || undefined,
      deliveryAddress: cleanObject(restData?.deliveryAddress, { removeEmptyStrings: false, removeNull: true }),
      pickupAddress: cleanObject(restData?.pickupAddress, { removeEmptyStrings: false, removeNull: true }),
      couponId: restData?.couponId || undefined,
      deliveryCost: restData?.deliveryCost || '0',
      damageWaiver: restData?.damageWaiver || '0',
      laborFee: restData?.laborFee || '0',
      discountRate: restData?.discountRate || '0',
      mileage: restData?.mileage || '0',
      taxRate: restData?.taxRate || '0',
      orderGroups: restData?.orderGroups?.map(({ id, idDetail, ...rest }) => {
        const convertData = {
          ...rest,
          discountRate: rest.discountRate || '0',
          position: rest.position || undefined,
        };

        const shouldAddId = idDetail && !isNaN(Number(id));

        return {
          ...convertData,
          ...(shouldAddId ? { id: idDetail } : {}),
        };
      }),
      orderItems: restData?.orderItems?.map(
        ({
          id,
          category,
          categoryId,
          categoryName,
          idDetail,
          type,
          parentId,
          isDelete,
          availableInventory,
          productId,
          isOverbooking,
          productDesignation,
          ...rest
        }) => {
          const newItem = cleanObject(rest, { removeEmptyStrings: false, removeNull: true }) as IOrderItem;

          const convertData = {
            ...newItem,
            laborCost: rest.laborCost || '0',
            discountRate: rest.discountRate || '0',
            variantId: rest.variantId || undefined,
            variantName: rest.variantName || undefined,
            position: rest.position || undefined,
            note: rest.note || EMPTY_STRING,
            total: rest.total || 0,
            overbookedQuantity: rest.overbookedQuantity || 0,
          };
          return idDetail ? { id: idDetail, ...convertData } : convertData;
        }
      ),
      orderAddOns: restData?.orderAddOns
        ?.filter((item) => item?.isShow)
        ?.map(
          ({
            id,
            idDetail,
            type,
            parentId,
            availableInventory,
            isOverbooking,
            productId,
            isShow,
            productDesignation,
            idRemember,
            isHasVariant,
            ...rest
          }) => {
            const newItem = cleanObject(rest, { removeEmptyStrings: false, removeNull: true }) as IOrderItemAddOn;

            const convertData = {
              ...newItem,
              laborCost: rest.laborCost || '0',
              discountRate: rest.discountRate || '0',
              variantId: rest.variantId || undefined,
              variantName: rest.variantName || undefined,
              position: rest.position || undefined,
              note: rest.note || EMPTY_STRING,
              total: rest.total || 0,
              overbookedQuantity: rest.overbookedQuantity || 0,
              discountType: rest?.discountType || DiscountTypeEnum.PERCENTAGE,
            };
            return idDetail ? { id: idDetail, ...convertData } : convertData;
          }
        ),

      ...(customFees && {
        customFees: customFees.map(({ id, ...rest }) => {
          return id && id.length > 10 ? rest : { id, ...rest };
        }),
      }),

      ...(clientAddress &&
        ({
          clientAddress: {
            address: clientAddress.address ?? EMPTY_STRING,
            unitDetail: clientAddress.unitDetail ?? EMPTY_STRING,
            city: clientAddress.city ?? EMPTY_STRING,
            state: clientAddress.state ?? EMPTY_STRING,
            postalCode: clientAddress.postalCode ?? EMPTY_STRING,
            // country: clientAddress.country ?? EMPTY_STRING,
            siteType: clientAddress.siteType ?? EMPTY_STRING,
            lat: clientAddress.lat ?? EMPTY_STRING,
            lng: clientAddress.lng ?? EMPTY_STRING,
          },
        } as IBaseAddress)),

      ...(orderFile && {
        orderFile: orderFile.filter((item) => !item.id),
      }),
      ...(removeFileIds.length && { deleteFileIds: removeFileIds }),
      ...(removeCustomIds.length && { deleteCustomIds: removeCustomIds }),
    };

    const formData = await createFormData(convertData, ['orderFile']);

    const params: IBodyUpdate<FormData> = {
      id: orderId || EMPTY_STRING,
      body: formData,
    };

    dispatch(updateOrder(params))
      .unwrap()
      .then((_res) => {
        handleCancel();
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCancel = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
    reset();
  };

  const handleRemoveFile = (fileId: string) => {
    setRemoveFileIds((prev) => [...prev, fileId]);
  };

  const handleRemoveCustom = (customId: string) => {
    if (customId.length > 15) return;

    setRemoveCustomIds((prev) => [...prev, customId]);
  };
  //#endregion Handle Function

  return (
    <Box id='orderPage' className={cx('container')}>
      <BaseToolbar title={t('form_order_title_edit_order')} isBack></BaseToolbar>

      <FormProvider
        {...({
          control,
          setError,
          resetField,
          watch,
          reset,
          trigger,
          setValue,
          getValues,
          formState: { errors, isDirty, dirtyFields, touchedFields, isSubmitted, defaultValues },
        } as UseFormReturn<IFormOrder>)}
      >
        <TemplateOrderForm />

        <ClientInformationOrderForm />

        <EventDetailsOrderForm
          managedByNameDefault={managedByNameDefault}
          salesPersonNameDefault={salesPersonNameDefault}
        />

        <EventTimelineOrderForm />

        <OrderItemsForm />

        {listOrderItems && <GroupsOrderForm />}

        <NotesOrderForm />

        <AttachmentsOrderForm onRemoveFile={handleRemoveFile} />

        <AdditionalChargesOrderForm couponNameDefault={couponNameDefault} onRemoveCustom={handleRemoveCustom} />

        <SummaryOrderForm />
      </FormProvider>
      <Stack
        direction={'row'}
        gap={3}
        alignItems={'center'}
        justifyContent={'flex-end'}
        sx={{
          // position: 'sticky',
          bottom: 0,
          bgColor: 'background.paper',
          p: 2,
          borderTop: '1px solid #ddd',
        }}
      >
        <Button variant='outlined' className={cx('buttonSaveDraft')} onClick={handleCancel}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>
        <Button
          variant='contained'
          className={cx('buttonSendOrder')}
          onClick={handleSubmit((data) => handleFormOrder(data))}
        >
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </Stack>

      {isLoading && <Loading />}
    </Box>
  );
};

export default OrderForm;
